import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Tag, Globe2, Users, Headphones, Wand2, Briefcase, Plane, FileCheck, Hotel, Palmtree, Car, Umbrella, Star, Clock, CalendarCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { waLink, formatApiErrorDetail } from "@/lib/api";
import { SectionHeading } from "@/components/SectionHeading";
import { Hero } from "@/components/home/Hero";
import { BookingSearch } from "@/components/home/BookingSearch";
import { TrustStrip } from "@/components/home/TrustStrip";
import { Counters } from "@/components/home/Counters";
import { TestimonialsCarousel } from "@/components/home/TestimonialsCarousel";
import { PartnersMarquee } from "@/components/home/PartnersMarquee";
import { FAQSection } from "@/components/home/FAQSection";
import { TravelTools } from "@/components/home/TravelTools";
import { DESTINATIONS } from "@/lib/siteData";
import { useContent } from "@/lib/useContent";
import { usePageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { GoogleReviews } from "@/components/home/GoogleReviews";
import { ClientGallery } from "@/components/home/ClientGallery";
import { VisaApprovals } from "@/components/home/VisaApprovals";
import { FlightPromos } from "@/components/home/FlightPromos";

const WHY = [
  { icon: Zap, t: "Fast Visa Assistance", d: "UAE visas in as little as 24 hours — handled end-to-end." },
  { icon: Tag, t: "Competitive Pricing", d: "Direct partner rates with no hidden fees, ever." },
  { icon: Globe2, t: "Worldwide Destinations", d: "45+ countries served across every continent." },
  { icon: Users, t: "Experienced Travel Consultants", d: "A decade of combined UAE & global travel expertise." },
  { icon: Wand2, t: "Tailor-Made Holidays", d: "Every itinerary crafted around you, not a template." },
  { icon: Briefcase, t: "Corporate Travel Solutions", d: "Dedicated account management for businesses." },
  { icon: Headphones, t: "Responsive Customer Support", d: "Real humans on WhatsApp, before, during and after your trip." },
];

const BADGE_STYLES = {
  "Best Seller": "bg-[#D4A62A] text-white",
  "Luxury": "bg-[#0A2E4D] text-[#E3BA45]",
  "Family Favourite": "bg-emerald-600 text-white",
};

const SERVICES = [
  { icon: Plane, title: "Flights", desc: "International & domestic flights on all major airlines — one-way, return and multi-city.", to: "/flights" },
  { icon: FileCheck, title: "Tourist Visas", desc: "Dubai, UK, Schengen, USA, Canada & Australia — documents reviewed before submission.", to: "/visa" },
  { icon: Hotel, title: "Hotels", desc: "Worldwide hotel reservations, from boutique stays to 5-star beachfront resorts.", to: "/hotels" },
  { icon: Palmtree, title: "Holiday Packages", desc: "Luxury holidays, family escapes, honeymoons and group tours — fully arranged.", to: "/holidays" },
  { icon: Car, title: "Airport Transfers", desc: "Premium private transfers across the UAE, Zimbabwe and South Africa.", to: "/transfers" },
  { icon: Umbrella, title: "Travel Insurance", desc: "Comprehensive cover for medical, cancellations and baggage — required for Schengen.", to: "/insurance" },
  { icon: Briefcase, title: "Corporate Travel", desc: "Business travel programmes with dedicated consultants and negotiated rates.", to: "/corporate" },
];

const FlightEnquiry = () => {
  const [f, setF] = useState({ full_name: "", country_code: "+263", phone: "", email: "", departure_city: "", destination: "", travel_date: "", return_date: "", adults: "1", children: "0", cabin: "Economy", trip_type: "Return", flexible: false, airline: "", requests: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/flight", {
        full_name: f.full_name, email: f.email, phone: `${f.country_code} ${f.phone}`.trim(), departure_city: f.departure_city, destination: f.destination,
        travel_date: f.travel_date, return_date: f.return_date, trip_type: f.trip_type.toLowerCase(),
        num_passengers: (parseInt(f.adults) || 1) + (parseInt(f.children) || 0),
        notes: `Email: ${f.email} | Adults: ${f.adults}, Children: ${f.children}, Cabin: ${f.cabin} | Trip: ${f.trip_type}${f.flexible ? " (flexible dates)" : ""}${f.airline ? ` | Preferred airline: ${f.airline}` : ""}${f.requests ? ` | Special requests: ${f.requests}` : ""}`,
      });
      trackEvent("enquiry_submitted", { form: "flight_home" });
      toast.success("Enquiry received! A consultant will send your best fare shortly.");
      setF({ full_name: "", country_code: "+263", phone: "", email: "", departure_city: "", destination: "", travel_date: "", return_date: "", adults: "1", children: "0", cabin: "Economy", trip_type: "Return", flexible: false, airline: "", requests: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cls = "w-full rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4A62A] [color-scheme:dark]";
  const lbl = "text-[11px] font-semibold uppercase tracking-wider text-white/60 mb-1.5 block";
  return (
    <section className="py-24 lg:py-32 bg-[#0A2E4D]" data-testid="flight-enquiry-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-14 items-center">
        <div className="lg:col-span-2">
          <span className="mk-gold-line mb-6 block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#E3BA45] font-bold mb-3">Flight Enquiry</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">Tell us where. We'll find the best fare.</h2>
          <p className="text-slate-300 mt-5 leading-relaxed">Our consultants compare fares across every major airline and reply within hours — not days. No booking fees on enquiries.</p>
          <div className="mt-8 space-y-3 text-sm text-slate-300">
            <div className="flex items-center gap-3"><Clock size={16} className="text-[#E3BA45]" /> Replies within business hours, 7 days a week</div>
            <div className="flex items-center gap-3"><Star size={16} className="text-[#E3BA45]" /> Group & corporate fares available</div>
            <div className="flex items-center gap-3"><CalendarCheck size={16} className="text-[#E3BA45]" /> Flexible date searches on request</div>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-3 bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 grid grid-cols-2 gap-4 backdrop-blur">
          <div><label className={lbl}>Full Name</label><input required value={f.full_name} onChange={set("full_name")} className={cls} placeholder="John Moyo" data-testid="fe-name-input" /></div>
          <div>
            <label className={lbl}>Phone / WhatsApp</label>
            <div className="flex gap-2">
              <select value={f.country_code} onChange={set("country_code")} className={`${cls} !w-24 !px-2`} data-testid="fe-country-code-select">
                {["+263", "+971", "+27", "+260", "+254", "+234", "+267", "+44", "+1", "+91"].map((c) => <option key={c} className="text-black">{c}</option>)}
              </select>
              <input required value={f.phone} onChange={set("phone")} className={cls} placeholder="777 000 000" data-testid="fe-phone-input" />
            </div>
          </div>
          <div className="col-span-2"><label className={lbl}>Email</label><input type="email" value={f.email} onChange={set("email")} className={cls} placeholder="you@email.com" data-testid="fe-email-input" /></div>
          <div><label className={lbl}>From</label><input required value={f.departure_city} onChange={set("departure_city")} className={cls} placeholder="Harare" data-testid="fe-from-input" /></div>
          <div><label className={lbl}>Destination</label><input required value={f.destination} onChange={set("destination")} className={cls} placeholder="Dubai" data-testid="fe-dest-input" /></div>
          <div><label className={lbl}>Trip Type</label><select value={f.trip_type} onChange={set("trip_type")} className={cls} data-testid="fe-trip-type-select">{["Return", "One-way", "Multi-city"].map((t) => <option key={t} className="text-black">{t}</option>)}</select></div>
          <div><label className={lbl}>Cabin Class</label><select value={f.cabin} onChange={set("cabin")} className={cls}>{["Economy", "Premium Economy", "Business", "First"].map((c) => <option key={c} className="text-black">{c}</option>)}</select></div>
          <div><label className={lbl}>Departure Date</label><input type="date" value={f.travel_date} onChange={set("travel_date")} className={cls} data-testid="fe-depart-input" /></div>
          <div><label className={lbl}>Return Date</label><input type="date" value={f.return_date} onChange={set("return_date")} className={cls} /></div>
          <div><label className={lbl}>Adults</label><select value={f.adults} onChange={set("adults")} className={cls}>{[1,2,3,4,5,6,7,8].map((n) => <option key={n} className="text-black">{n}</option>)}</select></div>
          <div><label className={lbl}>Children</label><select value={f.children} onChange={set("children")} className={cls}>{[0,1,2,3,4,5].map((n) => <option key={n} className="text-black">{n}</option>)}</select></div>
          <div><label className={lbl}>Preferred Airline</label><input value={f.airline} onChange={set("airline")} className={cls} placeholder="e.g. Emirates (optional)" data-testid="fe-airline-input" /></div>
          <div className="flex items-end pb-2.5">
            <label className="flex items-center gap-2.5 text-sm text-white/80 cursor-pointer">
              <input type="checkbox" checked={f.flexible} onChange={set("flexible")} className="w-4 h-4 accent-[#D4A62A]" data-testid="fe-flexible-checkbox" /> My dates are flexible
            </label>
          </div>
          <div className="col-span-2"><label className={lbl}>Special Requests</label><textarea rows={2} value={f.requests} onChange={set("requests")} className={cls} placeholder="Wheelchair assistance, extra baggage, seat preferences…" data-testid="fe-requests-input" /></div>
          <button type="submit" disabled={busy} className="mk-btn-gold col-span-2 !py-4 disabled:opacity-60" data-testid="fe-submit-btn">{busy ? "Sending…" : "Get My Best Fare"}</button>
        </form>
      </div>
    </section>
  );
};

export default function Home() {
  const packages = useContent("packages");
  const visaServices = useContent("visa_services");
  const blogs = useContent("blogs");
  usePageMeta(null);
  return (
    <div>
      <Hero />
      <BookingSearch />
      <TrustStrip />

      {/* WHY CHOOSE */}
      <section className="py-24 lg:py-32 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Why Choose Makchester" title="A travel partner you can trust" subtitle="Premium service standards across three registered offices and 45+ destinations." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w) => (
              <div key={w.t} className="mk-card p-8" data-testid={`why-card-${w.t.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-12 h-12 rounded-xl bg-[#0A2E4D] flex items-center justify-center"><w.icon size={20} className="text-[#E3BA45]" strokeWidth={1.5} /></div>
                <h3 className="font-serif-display text-lg text-[#0A2E4D] mt-5">{w.t}</h3>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 lg:py-32 bg-[#F9FAFB]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Our Services" title="Everything for your next journey" subtitle="One consultant handles every part of your trip — from visa to touchdown." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <Link key={s.title} to={s.to} className="mk-card p-8 group" data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <s.icon size={30} className="text-[#D4A62A]" strokeWidth={1.5} />
                <h3 className="font-serif-display text-xl text-[#0A2E4D] mt-5">{s.title}</h3>
                <p className="text-sm text-[#4B5563] mt-2.5 leading-relaxed">{s.desc}</p>
                <div className="mt-5 text-sm text-[#0A2E4D] font-semibold inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">Book Now <ArrowRight size={14} className="text-[#D4A62A]" /></div>
              </Link>
            ))}
            <div className="mk-card p-8 bg-[#0A2E4D] !border-[#0A2E4D] flex flex-col justify-center">
              <h3 className="font-serif-display text-xl text-white">Not sure what you need?</h3>
              <p className="text-sm text-slate-300 mt-2.5">Chat with a consultant and we'll plan it together.</p>
              <a href={waLink("Hello Makchester, I need help planning my trip.")} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-[#E3BA45] font-semibold text-sm"><MessageCircle size={15} /> WhatsApp Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED DESTINATIONS */}
      <section className="py-24 lg:py-32 bg-white" data-testid="destinations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Featured Destinations" title="Where will you go next?" subtitle="Hand-picked destinations loved by our clients — flights, visas and hotels all arranged." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {DESTINATIONS.map((d) => (
              <Link key={d.name} to={`/holidays/request?destination=${encodeURIComponent(d.name)}`} className="group relative rounded-2xl overflow-hidden aspect-[3/4] block" data-testid={`destination-card-${d.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <img src={d.image} alt={`${d.name} — ${d.region} travel destination`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />
                <div className="absolute top-4 right-4 bg-white/15 backdrop-blur-md border border-white/25 rounded-full px-3.5 py-1.5 text-xs text-white font-semibold">from ${d.price}</div>
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#E3BA45] font-bold">{d.region}</div>
                  <h3 className="font-serif-display text-2xl text-white mt-1">{d.name}</h3>
                  <p className="text-white/80 text-xs mt-2 leading-relaxed line-clamp-2">{d.desc}</p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowRight size={14} className="text-[#E3BA45]" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOLIDAY PACKAGES */}
      <section className="py-24 lg:py-32 bg-[#F9FAFB]" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Holiday Packages" title="Curated escapes, fully arranged" subtitle="Hotels, transfers and experiences bundled at partner rates — just pack your bags." />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((p) => (
              <div key={p.name} className="mk-card overflow-hidden flex flex-col" data-testid={`package-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="relative h-56 overflow-hidden">
                  <img src={p.image} alt={`${p.name} holiday package`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  <div className="absolute top-4 left-4 bg-[#0A2E4D]/85 backdrop-blur text-white rounded-full px-3.5 py-1.5 text-xs font-semibold">{p.duration}</div>
                  {p.badge && <div className={`absolute top-4 right-4 rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider ${BADGE_STYLES[p.badge] || "bg-[#D4A62A] text-white"}`} data-testid={`package-badge-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>{p.badge}</div>}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-0.5">{Array.from({ length: p.rating }).map((_, i) => <Star key={i} size={13} className="text-[#D4A62A] fill-[#D4A62A]" />)}</div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${p.flights_included ? "bg-emerald-50 text-emerald-700" : "bg-[#F3F4F6] text-[#4B5563]"}`}>{p.flights_included ? "Flights Included" : "Land Package"}</span>
                  </div>
                  <h3 className="font-serif-display text-xl text-[#0A2E4D] mt-2">{p.name}</h3>
                  <p className="text-xs text-[#4B5563] mt-1">{p.destination}{p.package_type ? ` · ${p.package_type}` : ""}</p>
                  {p.departure_city && <p className="text-xs text-[#4B5563] mt-1">Departing: <span className="font-semibold text-[#0A2E4D]">{p.departure_city}</span></p>}
                  <ul className="mt-4 space-y-1.5 flex-1">
                    {p.includes.map((inc) => <li key={inc} className="text-[13px] text-[#4B5563] flex items-start gap-2"><span className="text-[#D4A62A] mt-0.5">✓</span>{inc}</li>)}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-[#E5E7EB] flex items-center justify-between">
                    <div>
                      <div className="text-[11px] text-[#4B5563] uppercase tracking-wider">Starting From</div>
                      <div className="font-serif-display text-2xl text-[#0A2E4D]">${p.price}<span className="text-xs text-[#4B5563] font-sans"> /person</span></div>
                      <div className="text-[10px] text-[#9CA3AF] mt-0.5">{p.validity ? `${p.validity} · ` : ""}Terms apply</div>
                    </div>
                    <Link to={`/holidays/request?destination=${encodeURIComponent(p.destination)}`} className="mk-btn-gold !px-5 !py-2.5 !text-xs">Book Now</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISA SERVICES */}
      <section className="py-24 lg:py-32 bg-white" data-testid="visa-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Visa Services" title="Visas made simple" subtitle="Document review, submission and tracking — handled by consultants who do this daily." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visaServices.map((v) => (
              <div key={v.country} className="mk-card p-8" data-testid={`visa-card-${v.country.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-serif-display text-xl text-[#0A2E4D]">{v.country}</h3>
                  <span className="text-2xl" aria-hidden>{v.flag}</span>
                </div>
                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between border-b border-dashed border-[#E5E7EB] pb-2"><span className="text-[#4B5563]">Processing</span><span className="font-semibold text-[#0A2E4D]">{v.processing}</span></div>
                  <div className="flex justify-between border-b border-dashed border-[#E5E7EB] pb-2 pt-1"><span className="text-[#4B5563]">Validity</span><span className="font-semibold text-[#0A2E4D]">{v.validity}</span></div>
                  {v.price && <div className="flex justify-between border-b border-dashed border-[#E5E7EB] pb-2 pt-1"><span className="text-[#4B5563]">Service Fee</span><span className="font-semibold text-[#D4A62A]">{v.price}</span></div>}
                </div>
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wider text-[#4B5563] font-bold mb-2">Documents Required</div>
                  <ul className="space-y-1">{v.docs.map((doc) => <li key={doc} className="text-[13px] text-[#4B5563] flex items-start gap-2"><span className="text-[#D4A62A] mt-0.5">•</span>{doc}</li>)}</ul>
                </div>
                <Link to="/visa" className="mt-6 mk-btn-navy w-full !py-3 !text-xs">Apply Now</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <VisaApprovals />
      <FlightPromos />
      <FlightEnquiry />
      <TestimonialsCarousel />
      <GoogleReviews />
      <ClientGallery />
      <Counters />

      {/* ABOUT TEASER */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-teaser-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-serif-display italic text-[#D4A62A] text-sm mb-2" data-testid="about-teaser-tagline">Your Gateway to the World</p>
            <SectionHeading center={false} eyebrow="About Makchester" title="Your trusted international travel partner" />
            <p className="text-[#4B5563] leading-relaxed -mt-8">Makchester Tours &amp; Travel is an international travel company registered in the United Arab Emirates, Zimbabwe and South Africa, serving travellers worldwide. Our mission is to make travel simple, affordable and unforgettable — with global travel solutions, clear communication and genuine care at every step.</p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[{ c: "United Arab Emirates", f: "🇦🇪" }, { c: "Zimbabwe", f: "🇿🇼" }, { c: "South Africa", f: "🇿🇦" }].map(({ c, f }) => (
                <div key={c} className="border border-[#E5E7EB] rounded-xl p-4 text-center">
                  <div className="text-xl" aria-hidden>{f}</div>
                  <div className="text-xs font-semibold text-[#0A2E4D] mt-1.5 leading-tight">{c}</div>
                  <div className="text-[10px] text-[#4B5563] mt-1">Registered</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0A2E4D] border-b-2 border-[#D4A62A] pb-1 hover:gap-3 transition-all" data-testid="about-read-more-link">Read our story <ArrowRight size={14} /></Link>
          </div>
          <div className="relative">
            <img src="https://images.pexels.com/photos/1268855/pexels-photo-1268855.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Luxury travel" loading="lazy" className="w-full h-[500px] object-cover rounded-2xl" />
            <div className="absolute -bottom-6 -left-6 bg-[#0A2E4D] text-white p-7 rounded-2xl hidden sm:block max-w-xs shadow-xl">
              <div className="text-[#E3BA45] text-xs uppercase tracking-[0.2em] mb-2">Three offices, one promise</div>
              <div className="font-serif-display text-lg leading-snug">Travel made simple, affordable and unforgettable.</div>
            </div>
          </div>
        </div>
      </section>

      <PartnersMarquee />

      {/* BLOG */}
      <section className="py-24 lg:py-32 bg-[#F9FAFB]" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Travel Blog" title="Tips, guides & visa updates" subtitle="Fresh insights from our consultants to help you travel smarter." />
          <div className="grid md:grid-cols-3 gap-8">
            {blogs.slice(0, 3).map((b) => (
              <Link key={b.slug} to={`/blog/${b.slug}`} className="mk-card overflow-hidden group" data-testid={`blog-card-${b.slug}`}>
                <div className="h-52 overflow-hidden"><img src={b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                <div className="p-7">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4A62A] font-bold">{b.category}</div>
                  <h3 className="font-serif-display text-lg text-[#0A2E4D] mt-2 leading-snug">{b.title}</h3>
                  <p className="text-[13px] text-[#4B5563] mt-2.5 leading-relaxed line-clamp-2">{b.excerpt}</p>
                  <div className="text-xs text-[#9CA3AF] mt-4">{b.date} · {b.read}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12"><Link to="/blog" className="mk-btn-navy" data-testid="blog-view-all-btn">View All Articles <ArrowRight size={15} /></Link></div>
        </div>
      </section>

      <TravelTools />
      <FAQSection />

      {/* CTA */}
      <section className="relative py-28 lg:py-36 overflow-hidden" data-testid="cta-section">
        <img src="https://images.unsplash.com/photo-1551918120-9739cb430c6d?q=80&w=1920&auto=format&fit=crop" alt="Maldives" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#051A2E]/95 via-[#0A2E4D]/85 to-[#0A2E4D]/60" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="mk-gold-line mb-6 inline-block" />
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-6xl text-white leading-tight">Ready for your next adventure?</h2>
          <p className="text-slate-200 mt-5 text-lg">Book with Makchester Tours &amp; Travel today.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/holidays/request" className="mk-btn-gold !px-9 !py-4" data-testid="cta-book-btn">Book Now</Link>
            <a href={waLink("Hello Makchester, I'm ready to book my next adventure!")} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-2 rounded-full px-9 py-4 text-sm font-semibold text-white bg-[#25D366] hover:bg-[#1ebe57] transition-colors" data-testid="cta-whatsapp-btn">
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
